import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const TIPOS_SERVICIO = [
  'Técnico aire acondicionado', 'Técnico internet / telefonía', 'Técnico electricidad',
  'Técnico plomería', 'Técnico ascensor', 'Técnico seguridad / CCTV',
  'Carpintería de aluminio', 'Carpintería de madera',
  'Flete / mudanza', 'Limpieza', 'Mantenimiento general', 'Otro',
]

// Panel de archivos separado para evitar re-renders
function ArchivosPanel({ gremioId, usuarioId }) {
  const [archivos, setArchivos] = useState([])
  const [uploading, setUploading] = useState(false)

  useEffect(() => { fetchArchivos() }, [gremioId])

  const fetchArchivos = async () => {
    const { data } = await supabase.from('archivos_gremios').select('*')
      .eq('gremio_id', gremioId).order('created_at', { ascending: false })
    setArchivos(data || [])
  }

  const handleUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo no puede superar los 10 MB')
      return
    }
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `${gremioId}/${Date.now()}.${ext}`

    const { error: uploadError } = await supabase.storage.from('documentos-gremios').upload(path, file)
    if (uploadError) {
      alert('Error al subir el archivo: ' + uploadError.message)
      setUploading(false)
      return
    }

    const { data: urlData } = supabase.storage.from('documentos-gremios').getPublicUrl(path)

    await supabase.from('archivos_gremios').insert({
      gremio_id: gremioId, usuario_id: usuarioId,
      nombre_archivo: file.name, url: urlData.publicUrl,
      tipo: file.type, tamano: file.size,
    })

    e.target.value = ''
    fetchArchivos()
    setUploading(false)
  }

  const handleDelete = async (archivo) => {
    if (!window.confirm(`¿Eliminar "${archivo.nombre_archivo}"?`)) return
    const path = archivo.url.split('/documentos-gremios/')[1]
    if (path) await supabase.storage.from('documentos-gremios').remove([path])
    await supabase.from('archivos_gremios').delete().eq('id', archivo.id)
    fetchArchivos()
  }

  const formatSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`
  const iconArchivo = (tipo) => {
    if (tipo?.includes('pdf')) return '📄'
    if (tipo?.includes('image')) return '🖼️'
    if (tipo?.includes('word') || tipo?.includes('document')) return '📝'
    return '📎'
  }

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 10 }}>
        DOCUMENTACIÓN · {archivos.length} archivo{archivos.length !== 1 ? 's' : ''}
      </div>
      {archivos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {archivos.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8 }}>
              <span style={{ fontSize: 18 }}>{iconArchivo(a.tipo)}</span>
              <a href={a.url} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, color: 'var(--text)', textDecoration: 'none', fontSize: 13 }}>
                <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.nombre_archivo}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>{formatSize(a.tamano)} · {new Date(a.created_at).toLocaleDateString('es-AR')}</div>
              </a>
              <button onClick={() => handleDelete(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: '2px 6px' }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <label className="btn btn-ghost btn-sm" style={{ cursor: uploading ? 'wait' : 'pointer' }}>
        {uploading ? 'Subiendo...' : '📎 Adjuntar archivo'}
        <input type="file" onChange={handleUpload} disabled={uploading}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
          style={{ display: 'none' }} />
      </label>
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>PDF, imágenes o documentos · Máx 10 MB</div>
    </div>
  )
}

export default function Gremios() {
  const { usuario } = useAuth()
  const [registros, setRegistros] = useState([])
  const [oficinas, setOficinas] = useState([])
  const [empresasPorTipo, setEmpresasPorTipo] = useState({})
  const [tecnicosPorEmpresa, setTecnicosPorEmpresa] = useState({})
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0])
  const [edificioFilter, setEdificioFilter] = useState('all')
  const [tipoFilter, setTipoFilter] = useState('all')
  const [periodoFilter, setPeriodoFilter] = useState('dia') // dia | mes | anio
  const [searchEmpresa, setSearchEmpresa] = useState('')
  const [edificios, setEdificios] = useState([])
  const [esConsorcial, setEsConsorcial] = useState(false)
  const [edificioConsorcial, setEdificioConsorcial] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [form, setForm] = useState({
    tipo_servicio: '', tipo_custom: '', empresa: '',
    tecnico_nombre: '', tecnico_dni: '', oficina_id: '', observaciones: ''
  })

  const isAdmin = usuario?.rol === 'admin'
  const edificioId = isAdmin ? null : usuario?.edificio_id

  useEffect(() => {
    fetchOficinas(); fetchRegistros(); fetchEmpresasYTecnicos()
    if (isAdmin) fetchEdificios()
  }, [])

  useEffect(() => { fetchRegistros() }, [dateFilter, edificioFilter, tipoFilter, periodoFilter])

  const fetchEdificios = async () => {
    const { data } = await supabase.from('edificios').select('*')
    setEdificios(data || [])
  }

  const fetchOficinas = async () => {
    let q = supabase.from('oficinas').select('id, numero, titular, email_contacto, email_contacto_2, edificio_id, edificios(nombre)').eq('activa', true).order('numero')
    if (edificioId) q = q.eq('edificio_id', edificioId)
    const { data } = await q
    setOficinas(data || [])
  }

  const fetchEmpresasYTecnicos = async () => {
    const { data } = await supabase.from('gremios').select('tipo_servicio, empresa, tecnico_nombre, tecnico_dni')
    const empresasGrouped = {}
    const tecnicosGrouped = {}
    data?.forEach(r => {
      if (r.empresa) {
        if (!empresasGrouped[r.tipo_servicio]) empresasGrouped[r.tipo_servicio] = new Set()
        empresasGrouped[r.tipo_servicio].add(r.empresa)

        if (r.tecnico_nombre) {
          const key = `${r.tipo_servicio}|${r.empresa}`
          if (!tecnicosGrouped[key]) tecnicosGrouped[key] = new Map()
          tecnicosGrouped[key].set(r.tecnico_nombre, r.tecnico_dni || '')
        }
      }
    })

    const empresasResult = {}
    Object.keys(empresasGrouped).forEach(k => { empresasResult[k] = Array.from(empresasGrouped[k]).sort() })
    setEmpresasPorTipo(empresasResult)

    const tecnicosResult = {}
    Object.keys(tecnicosGrouped).forEach(k => {
      tecnicosResult[k] = Array.from(tecnicosGrouped[k].entries()).map(([nombre, dni]) => ({ nombre, dni })).sort((a, b) => a.nombre.localeCompare(b.nombre))
    })
    setTecnicosPorEmpresa(tecnicosResult)
  }

  const fetchRegistros = async () => {
    setLoading(true)
    let start, end
    const base = new Date(dateFilter)
    if (periodoFilter === 'dia') {
      start = `${dateFilter}T00:00:00`
      end = `${dateFilter}T23:59:59`
    } else if (periodoFilter === 'mes') {
      const y = base.getFullYear(), m = base.getMonth()
      start = new Date(y, m, 1).toISOString()
      end = new Date(y, m + 1, 0, 23, 59, 59).toISOString()
    } else if (periodoFilter === 'anio') {
      const y = base.getFullYear()
      start = new Date(y, 0, 1).toISOString()
      end = new Date(y, 11, 31, 23, 59, 59).toISOString()
    }
    let q = supabase
      .from('gremios')
      .select('*, oficinas(numero, titular, email_contacto), edificios(nombre), usuarios(nombre)')
      .gte('timestamp', start).lte('timestamp', end).order('timestamp', { ascending: false })
    if (edificioId) q = q.eq('edificio_id', edificioId)
    else if (edificioFilter !== 'all') q = q.eq('edificio_id', edificioFilter)
    if (tipoFilter === 'oficina') q = q.not('oficina_id', 'is', null)
    else if (tipoFilter === 'consorcial') q = q.is('oficina_id', null)
    const { data } = await q
    // Filter by empresa search client-side
    const filtered = searchEmpresa
      ? (data || []).filter(r => 
          r.empresa?.toLowerCase().includes(searchEmpresa.toLowerCase()) ||
          r.tipo_servicio?.toLowerCase().includes(searchEmpresa.toLowerCase())
        )
      : (data || [])
    setRegistros(filtered)
    setLoading(false)
  }

  const handleSelectTecnico = (nombre) => {
    const tecnicosKey = `${form.tipo_servicio}|${form.empresa}`
    const tecnicos = tecnicosPorEmpresa[tecnicosKey] || []
    const t = tecnicos.find(x => x.nombre === nombre)
    setForm(f => ({ ...f, tecnico_nombre: nombre, tecnico_dni: t?.dni || '' }))
  }

  const handleSubmit = async () => {
    const tipoFinal = form.tipo_servicio === 'Otro' ? form.tipo_custom : form.tipo_servicio
    if (!tipoFinal) return
    if (!esConsorcial && !form.oficina_id) return
    setLoading(true)

    let edificioFinal = edificioId
    let oficina = null
    if (esConsorcial) {
      if (!edificioFinal) edificioFinal = edificioConsorcial
    } else {
      oficina = oficinas.find(o => o.id === form.oficina_id)
      edificioFinal = oficina.edificio_id
    }

    const { error } = await supabase.from('gremios').insert({
      tipo_servicio: tipoFinal,
      empresa: form.empresa || null,
      tecnico_nombre: form.tecnico_nombre || null,
      tecnico_dni: form.tecnico_dni || null,
      oficina_id: esConsorcial ? null : form.oficina_id,
      edificio_id: edificioFinal,
      usuario_id: usuario?.id,
      observaciones: form.observaciones || null,
    })

    if (!error) {
      if (!esConsorcial) {
        const emailsGremio = [oficina?.email_contacto, oficina?.email_contacto_2].filter(Boolean)
        for (const emailDest of emailsGremio) {
          fetch('/api/send-email', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tipo: 'gremio', destinatario_email: emailDest,
              oficina: oficina.numero, oficina_titular: oficina.titular,
              edificio: oficina.edificios?.nombre || '',
              datos: { tipo_servicio: tipoFinal, empresa: form.empresa, tecnico_nombre: form.tecnico_nombre, tecnico_dni: form.tecnico_dni, observaciones: form.observaciones }
            })
          }).catch(e => console.error(e))
        }
      }
      setSuccess(esConsorcial ? 'Visita consorcial registrada' : 'Visita técnica registrada')
      setTimeout(() => setSuccess(null), 3000)
      setForm({ tipo_servicio: '', tipo_custom: '', empresa: '', tecnico_nombre: '', tecnico_dni: '', oficina_id: '', observaciones: '' })
      setEsConsorcial(false); setEdificioConsorcial(''); setShowForm(false)
      fetchRegistros(); fetchEmpresasYTecnicos()
    }
    setLoading(false)
  }

  const formatTime = (ts) => new Date(ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  const formatDate = (ts) => new Date(ts).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })

  const iconoTipo = (tipo) => {
    if (tipo?.includes('aire')) return '❄️'
    if (tipo?.includes('internet') || tipo?.includes('telefonía')) return '📡'
    if (tipo?.includes('electricidad')) return '⚡'
    if (tipo?.includes('plomería')) return '🔧'
    if (tipo?.includes('ascensor')) return '🛗'
    if (tipo?.includes('seguridad')) return '📹'
    if (tipo?.includes('aluminio') || tipo?.includes('madera')) return '🪚'
    if (tipo?.includes('flete') || tipo?.includes('mudanza')) return '🚛'
    if (tipo?.includes('limpieza')) return '🧹'
    return '🔨'
  }

  const empresasSugeridas = form.tipo_servicio && form.tipo_servicio !== 'Otro' ? (empresasPorTipo[form.tipo_servicio] || []) : []
  const tecnicosSugeridos = (form.tipo_servicio && form.empresa) ? (tecnicosPorEmpresa[`${form.tipo_servicio}|${form.empresa}`] || []) : []

  return (
    <div className="fade-in">
      {success && (
        <div style={{ position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--green)', color: 'white', padding: '14px 28px', borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', animation: 'slideDown 0.3s ease', whiteSpace: 'nowrap' }}>
          ✓ {success}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 36, color: 'var(--text)', marginBottom: 4 }}>Gremios y Proveedores</h1>
          <p style={{ color: 'var(--text3)', fontSize: 14 }}>Visitas técnicas · {registros.length} registros</p>
        </div>
        <button className="btn btn-gold" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancelar' : '+ Registrar visita'}
        </button>
      </div>

      {showForm && (
        <div className="card fade-in" style={{ marginBottom: 24, borderColor: 'var(--gold)', borderWidth: 1.5 }}>
          <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 20 }}>NUEVA VISITA TÉCNICA</div>

          <div style={{
            background: esConsorcial ? 'var(--gold-dim)' : 'var(--bg3)',
            border: `1.5px solid ${esConsorcial ? 'var(--gold)' : 'var(--border)'}`,
            borderRadius: 10, padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer'
          }} onClick={() => setEsConsorcial(!esConsorcial)}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: esConsorcial ? 'var(--gold)' : 'var(--text)' }}>🏛 Visita consorcial (espacios comunes)</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                {esConsorcial ? 'No se asocia a una oficina ni se envía email' : 'Activar si la visita es para el edificio, no para una oficina específica'}
              </div>
            </div>
            <div style={{ width: 44, height: 24, borderRadius: 12, background: esConsorcial ? 'var(--gold)' : 'var(--border2)', position: 'relative', transition: 'all 0.2s' }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 3, left: esConsorcial ? 23 : 3, transition: 'all 0.2s' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="mobile-grid-1">
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>TIPO DE SERVICIO</label>
              <select className="input" value={form.tipo_servicio} onChange={e => setForm(f => ({ ...f, tipo_servicio: e.target.value, empresa: '', tecnico_nombre: '', tecnico_dni: '' }))}>
                <option value="">Seleccioná tipo...</option>
                {TIPOS_SERVICIO.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {form.tipo_servicio === 'Otro' && (
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>ESPECIFICÁ EL SERVICIO</label>
                <input className="input" placeholder="Ej: Técnico de gas" value={form.tipo_custom} onChange={e => setForm(f => ({ ...f, tipo_custom: e.target.value }))} />
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>EMPRESA / PROVEEDOR</label>
              <input className="input" placeholder="Nombre de la empresa" value={form.empresa}
                onChange={e => setForm(f => ({ ...f, empresa: e.target.value, tecnico_nombre: '', tecnico_dni: '' }))} list="empresas-sugeridas" />
              <datalist id="empresas-sugeridas">
                {empresasSugeridas.map(e => <option key={e} value={e} />)}
              </datalist>
              {empresasSugeridas.length > 0 && (
                <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 4, fontFamily: 'DM Mono, monospace' }}>
                  ✓ {empresasSugeridas.length} empresa{empresasSugeridas.length !== 1 ? 's' : ''} preestablecida{empresasSugeridas.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>
                NOMBRE DEL TÉCNICO {esConsorcial && '(opcional)'}
              </label>
              {tecnicosSugeridos.length > 0 ? (
                <select className="input" value={form.tecnico_nombre} onChange={e => {
                  if (e.target.value === '__nuevo__') setForm(f => ({ ...f, tecnico_nombre: '', tecnico_dni: '' }))
                  else handleSelectTecnico(e.target.value)
                }}>
                  <option value="">Seleccioná técnico...</option>
                  {tecnicosSugeridos.map(t => <option key={t.nombre} value={t.nombre}>{t.nombre}{t.dni ? ` · DNI ${t.dni}` : ''}</option>)}
                  <option value="__nuevo__">+ Cargar técnico nuevo</option>
                </select>
              ) : (
                <input className="input" placeholder="Nombre y apellido" value={form.tecnico_nombre} onChange={e => setForm(f => ({ ...f, tecnico_nombre: e.target.value }))} />
              )}
              {tecnicosSugeridos.length > 0 && form.tecnico_nombre === '' && (
                <input className="input" placeholder="Nombre y apellido del nuevo técnico" value={form.tecnico_nombre} onChange={e => setForm(f => ({ ...f, tecnico_nombre: e.target.value }))} style={{ marginTop: 8 }} />
              )}
              {tecnicosSugeridos.length > 0 && (
                <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 4, fontFamily: 'DM Mono, monospace' }}>
                  ✓ {tecnicosSugeridos.length} técnico{tecnicosSugeridos.length !== 1 ? 's' : ''} previamente registrado{tecnicosSugeridos.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>DNI DEL TÉCNICO {esConsorcial && '(opcional)'}</label>
              <input className="input" placeholder="Opcional" value={form.tecnico_dni} onChange={e => setForm(f => ({ ...f, tecnico_dni: e.target.value }))} />
            </div>

            {!esConsorcial && (
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>OFICINA QUE AUTORIZA</label>
                <select className="input" value={form.oficina_id} onChange={e => setForm(f => ({ ...f, oficina_id: e.target.value }))}>
                  <option value="">Seleccioná oficina...</option>
                  {oficinas.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.titular} {isAdmin ? `(${o.edificios?.nombre})` : ''}</option>)}
                </select>
              </div>
            )}

            {esConsorcial && isAdmin && (
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>EDIFICIO</label>
                <select className="input" value={edificioConsorcial} onChange={e => setEdificioConsorcial(e.target.value)}>
                  <option value="">Seleccioná edificio...</option>
                  {edificios.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </div>
            )}

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>OBSERVACIONES</label>
              <input className="input" placeholder="Ej: Revisión anual, no requiere acceso a sala de máquinas" value={form.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-outline btn-sm" onClick={() => { setShowForm(false); setEsConsorcial(false) }}>Cancelar</button>
            <button className="btn btn-gold btn-sm" onClick={handleSubmit}
              disabled={loading || (!form.tipo_servicio || (form.tipo_servicio === 'Otro' && !form.tipo_custom)) || (!esConsorcial && !form.oficina_id) || (esConsorcial && isAdmin && !edificioConsorcial)}>
              Registrar visita
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Periodo */}
        <div style={{ display: 'flex', gap: 6 }}>
          {[['dia','Día'],['mes','Mes'],['anio','Año']].map(([k,l]) => (
            <button key={k} className={`btn btn-sm ${periodoFilter === k ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setPeriodoFilter(k)}>{l}</button>
          ))}
        </div>
        {/* Fecha */}
        <input className="input" type={periodoFilter === 'anio' ? 'number' : periodoFilter === 'mes' ? 'month' : 'date'}
          value={periodoFilter === 'anio' ? new Date(dateFilter).getFullYear() : periodoFilter === 'mes' ? dateFilter.slice(0,7) : dateFilter}
          onChange={e => {
            if (periodoFilter === 'anio') setDateFilter(`${e.target.value}-01-01`)
            else if (periodoFilter === 'mes') setDateFilter(`${e.target.value}-01`)
            else setDateFilter(e.target.value)
          }} style={{ width: periodoFilter === 'anio' ? 100 : 160 }} />
        {/* Edificio */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={`btn btn-sm ${edificioFilter === 'all' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEdificioFilter('all')}>Todos</button>
            {edificios.map(e => (
              <button key={e.id} className={`btn btn-sm ${edificioFilter === e.id ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEdificioFilter(e.id)}>{e.nombre.replace('Essence ', '')}</button>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Tipo visita */}
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`btn btn-sm ${tipoFilter === 'all' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setTipoFilter('all')}>Todas</button>
          <button className={`btn btn-sm ${tipoFilter === 'oficina' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setTipoFilter('oficina')}>Por oficina</button>
          <button className={`btn btn-sm ${tipoFilter === 'consorcial' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setTipoFilter('consorcial')}>🏛 Consorciales</button>
        </div>
        {/* Buscar empresa/servicio */}
        <input className="input" placeholder="🔍 Buscar empresa o servicio..." value={searchEmpresa}
          onChange={e => { setSearchEmpresa(e.target.value); fetchRegistros() }}
          style={{ width: 260 }} />
        {searchEmpresa && (
          <button className="btn btn-ghost btn-sm" onClick={() => setSearchEmpresa('')}>✕ Limpiar</button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>Cargando...</div>
      ) : registros.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '64px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔧</div>
          <div style={{ color: 'var(--text3)', fontSize: 14 }}>Sin visitas técnicas registradas para este día</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {registros.map(r => (
            <div key={r.id} className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                  {iconoTipo(r.tipo_servicio)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{r.tipo_servicio}</span>
                    {r.oficina_id ? (
                      <span className="badge badge-gold" style={{ fontSize: 10 }}>Of. {r.oficinas?.numero}</span>
                    ) : (
                      <span className="badge" style={{ background: 'rgba(201,168,76,0.2)', color: 'var(--gold)', fontSize: 10 }}>🏛 CONSORCIAL</span>
                    )}
                    {isAdmin && <span className="badge badge-gray" style={{ fontSize: 10 }}>{r.edificios?.nombre?.replace('Essence ', '')}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 2 }}>
                    {r.empresa && <span style={{ marginRight: 12 }}>{r.empresa}</span>}
                    {r.tecnico_nombre && <span>👤 {r.tecnico_nombre}{r.tecnico_dni ? ` · DNI ${r.tecnico_dni}` : ''}</span>}
                  </div>
                  {r.observaciones && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{r.observaciones}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: 'var(--green)' }}>{formatTime(r.timestamp)}</div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--text3)' }}>{formatDate(r.timestamp)}</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ color: expandedId === r.id ? 'var(--gold)' : 'var(--text3)' }}
                    onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                    📎 {expandedId === r.id ? 'Ocultar' : 'Documentación'}
                  </button>
                </div>
              </div>
              {expandedId === r.id && <ArchivosPanel gremioId={r.id} usuarioId={usuario?.id} />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
